import React from 'react';

const SummaryDisplayChat = ({ content }) => {
  const processedContent = content.replace(/\\n/g, '\n');

  const hasMarkdown = (text) =>
    /^#{1,6}\s/.test(text) ||
    /\*\*.*\*\*/.test(text) ||
    /^\s*[-*]\s/.test(text) ||
    /^\|.*\|/.test(text);

  const parseInline = (text) => {
    const parts = text.split(/(\[.+?\]\(.+?\)|\*\*.+?\*\*)/g);
    return parts.map((part, i) => {
      const linkMatch = part.match(/^\[(.+?)\]\((.+?)\)$/);
      if (linkMatch) {
        const [, label, href] = linkMatch;
        return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="md-link">{label}</a>;
      }
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const renderTable = (rows, key) => {
    if (rows.length === 0) return null;
    const headerRow = rows[0];
    const dataRows = rows.slice(1);
    return (
      <div key={key} className="md-table-wrap">
        <table className="md-table">
          <thead>
            <tr>{headerRow.map((cell, i) => <th key={i}>{parseInline(cell.trim())}</th>)}</tr>
          </thead>
          <tbody>
            {dataRows.map((row, i) => (
              <tr key={i}>{row.map((cell, j) => <td key={j}>{parseInline(cell.trim())}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const parseMarkdown = (text) => {
    const lines = text.split('\n');
    const parsed = [];
    let inTable = false;
    let tableRows = [];
    let listItems = [];

    const flushList = (idx) => {
      if (listItems.length === 0) return;
      parsed.push(
        <ul key={`ul-${idx}`} className="md-ul">
          {listItems.map((item, i) => (
            <li key={i} className="md-li">
              <span className="md-li-bullet">▸</span>
              <span>{parseInline(item)}</span>
            </li>
          ))}
        </ul>
      );
      listItems = [];
    };

    lines.forEach((line, index) => {
      if (line.match(/^-{3,}\s*$/)) {
        flushList(index);
        // skip --- divider lines
      } else if (line.match(/^######\s/)) {
        flushList(index);
        parsed.push(<h6 key={index} className="md-h6">{parseInline(line.replace(/^######\s/, ''))}</h6>);
      } else if (line.match(/^#####\s/)) {
        flushList(index);
        parsed.push(<h5 key={index} className="md-h5">{parseInline(line.replace(/^#####\s/, ''))}</h5>);
      } else if (line.match(/^####\s/)) {
        flushList(index);
        parsed.push(<h4 key={index} className="md-h4">{parseInline(line.replace(/^####\s/, ''))}</h4>);
      } else if (line.match(/^###\s/)) {
        flushList(index);
        parsed.push(
          <h3 key={index} className="md-h3">
            <span className="md-h3-accent" />
            {parseInline(line.replace(/^###\s/, ''))}
          </h3>
        );
      } else if (line.match(/^##\s/)) {
        flushList(index);
        parsed.push(<h2 key={index} className="md-h2">{parseInline(line.replace(/^##\s/, ''))}</h2>);
      } else if (line.match(/^#\s/)) {
        flushList(index);
        parsed.push(<h1 key={index} className="md-h1">{parseInline(line.replace(/^#\s/, ''))}</h1>);
      } else if (line.match(/^\|.*\|$/)) {
        flushList(index);
        const cells = line.split('|').slice(1, -1).map(cell => cell.trim());
        if (!inTable) { inTable = true; tableRows = []; }
        if (!line.match(/^[|\s\-:]+$/)) tableRows.push(cells);
      } else if (inTable && tableRows.length > 0) {
        parsed.push(renderTable(tableRows, `table-${index}`));
        inTable = false;
        tableRows = [];
        if (line.trim()) parsed.push(<div key={index} className="md-line">{parseInline(line)}</div>);
      } else if (line.match(/^\\\*\s*/)) {
        flushList(index);
        parsed.push(<div key={index} className="md-note">{parseInline(line.replace(/^\\\*\s*/, ''))}</div>);
      } else if (line.match(/^\s*[-*]\s/)) {
        listItems.push(line.replace(/^\s*[-*]\s/, ''));
      } else if (line.trim()) {
        flushList(index);
        parsed.push(<div key={index} className="md-line">{parseInline(line)}</div>);
      } else {
        flushList(index);
        parsed.push(<div key={index} className="md-spacer" />);
      }
    });

    flushList('end');
    if (inTable && tableRows.length > 0) parsed.push(renderTable(tableRows, 'table-end'));
    return parsed;
  };

  const shouldUseMarkdown = hasMarkdown(processedContent);

  return (
    <>
      <style>{`
        /* ── Card shell ─────────────────────────────── */
        .ai-response-card {
          background: #ffffff;
          border: 1px solid rgba(6, 182, 212, 0.25);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.07);
          margin: 4px 0;
        }

        .ai-response-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 11px 18px;
          background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
        }

        .ai-avatar {
          width: 28px;
          height: 28px;
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: 0.03em;
          flex-shrink: 0;
        }

        .ai-response-title {
          font-size: 13px;
          font-weight: 700;
          color: rgba(255,255,255,0.9);
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        /* ── Body ───────────────────────────────────── */
        .ai-response-body {
          padding: 20px 24px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #1e293b;
        }

        /* ── Headings ───────────────────────────────── */
        .ai-response-body .md-h1 {
          font-size: 22px;
          font-weight: 700;
          color: #0f172a;
          margin: 20px 0 12px 0;
          padding-bottom: 7px;
          border-bottom: 2px solid #06b6d4;
          letter-spacing: -0.01em;
        }

        .ai-response-body .md-h2 {
          font-size: 19px;
          font-weight: 700;
          color: #0e7490;
          margin: 18px 0 10px 0;
          padding-bottom: 5px;
          border-bottom: 1px solid rgba(6, 182, 212, 0.3);
        }

        .ai-response-body .md-h3 {
          display: flex;
          align-items: center;
          gap: 9px;
          font-size: 16px;
          font-weight: 700;
          color: #0891b2;
          margin: 14px 0 7px 0;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .md-h3-accent {
          display: inline-block;
          width: 3px;
          height: 16px;
          background: #06b6d4;
          border-radius: 2px;
          flex-shrink: 0;
        }

        .ai-response-body .md-h4 {
          font-size: 15px;
          font-weight: 700;
          color: #0891b2;
          margin: 12px 0 6px 0;
        }

        .ai-response-body .md-h5 {
          font-size: 14px;
          font-weight: 700;
          color: #06b6d4;
          margin: 10px 0 5px 0;
        }

        .ai-response-body .md-h6 {
          font-size: 13px;
          font-weight: 700;
          color: #22d3ee;
          margin: 8px 0 4px 0;
        }

        /* ── Body text ──────────────────────────────── */
        .ai-response-body .md-line {
          font-size: 16px;
          line-height: 1.75;
          color: #334155;
          margin: 4px 0;
        }

        .ai-response-body .md-spacer {
          height: 10px;
        }

        /* ── Bullet lists ───────────────────────────── */
        .ai-response-body .md-ul {
          list-style: none;
          margin: 8px 0 12px 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .ai-response-body .md-li {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 16px;
          line-height: 1.65;
          color: #334155;
          background: #f0fdff;
          border-left: 3px solid rgba(6, 182, 212, 0.4);
          border-radius: 0 6px 6px 0;
          padding: 7px 12px;
        }

        .md-li-bullet {
          color: #0891b2;
          font-size: 12px;
          flex-shrink: 0;
          margin-top: 4px;
          font-weight: 700;
        }

        /* ── Subsection title lines (* prefix) ─────────── */
        .ai-response-body .md-note {
          font-size: 14px;
          font-weight: 600;
          line-height: 1.5;
          color: #475569;
          margin: 10px 0 3px 0;
          padding-left: 14px;
          border-left: 2px solid rgba(6, 182, 212, 0.35);
          background: none;
          border-radius: 0;
        }

        /* ── Bold ───────────────────────────────────── */
        .ai-response-body strong {
          font-weight: 700;
          color: #0e7490;
        }

        /* ── Tables ─────────────────────────────────── */
        .md-table-wrap {
          overflow-x: auto;
          margin: 14px 0;
          border-radius: 8px;
          border: 1px solid rgba(6, 182, 212, 0.2);
          box-shadow: 0 1px 5px rgba(0,0,0,0.06);
        }

        .md-table {
          width: max-content;
          min-width: 100%;
          border-collapse: collapse;
          font-size: 15px;
        }

        .md-table th {
          background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
          color: #ffffff;
          padding: 11px 16px;
          text-align: left;
          font-weight: 700;
          font-size: 14px;
          letter-spacing: 0.03em;
          white-space: nowrap;
        }

        .md-table td {
          padding: 10px 16px;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          font-size: 15px;
          white-space: nowrap;
        }

        .md-table tbody tr:hover {
          background: #f0fdff;
        }

        .md-table tbody tr:last-child td {
          border-bottom: none;
        }

        /* ── Feature links ──────────────────────────── */
        .md-link {
          display: inline-flex;
          align-items: center;
          padding: 3px 10px;
          margin: 0 3px;
          background: rgba(6, 182, 212, 0.08);
          border: 1px solid rgba(6, 182, 212, 0.35);
          border-radius: 20px;
          color: #0891b2;
          font-size: 13px;
          font-weight: 600;
          text-decoration: none;
          white-space: nowrap;
          transition: background 0.15s, border-color 0.15s;
        }
        .md-link:hover {
          background: rgba(6, 182, 212, 0.18);
          border-color: #0891b2;
          color: #0e7490;
        }

        /* ── Plain text card ────────────────────────── */
        .plain-text-card {
          background: #ffffff;
          border: 1px solid rgba(6, 182, 212, 0.25);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.07);
        }

        .plain-text-body {
          padding: 18px 24px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 16px;
          line-height: 1.75;
          color: #334155;
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        /* ── Mobile ─────────────────────────────────── */
        @media (max-width: 768px) {
          .ai-response-body {
            padding: 16px;
          }
          .ai-response-body .md-h1 { font-size: 20px; }
          .ai-response-body .md-h2 { font-size: 18px; }
          .ai-response-body .md-h3 { font-size: 15px; }
          .ai-response-body .md-h4 { font-size: 14px; }
          .ai-response-body .md-h5 { font-size: 13px; }
          .ai-response-body .md-h6 { font-size: 12px; }
          .ai-response-body .md-line,
          .ai-response-body .md-li  { font-size: 16px; }
          .md-table                  { font-size: 14px; }
          .md-table th, .md-table td { padding: 8px 10px; }
          .plain-text-body           { font-size: 16px; padding: 14px 16px; }
        }
      `}</style>

      {shouldUseMarkdown ? (
        <div className="ai-response-card">
          <div className="ai-response-body">
            {parseMarkdown(processedContent)}
          </div>
        </div>
      ) : (
        <div className="plain-text-card">
          <div className="plain-text-body">{processedContent}</div>
        </div>
      )}
    </>
  );
};

export default SummaryDisplayChat;
